import { useCallback, useEffect, useState } from 'react'
import {
  X, Loader2, Check, Ban, FileText, AlertTriangle, Undo2, CalendarClock, Users,
} from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { useMe } from '../../lib/me.js'
import { isHR } from '../../lib/role.js'
import { fullName } from '../../lib/employee.js'
import { leaveTypeMeta } from '../../lib/timetable.js'
import { shortType } from '../../lib/employeeMatrix.js'
import {
  statusLabel, statusTone, fmtRange, dateRange, skipReason, applyWarning,
  PERSONAL_REVIEW_DAYS, SICK_CERT_DAYS, LEAVE_PERSONAL, LEAVE_SICK,
} from '../../lib/leaveRequest.js'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * The leave DECISION drawer.
 *
 * Two things it shows before the buttons, and both are deliberate:
 *
 *  1. **What is already on the roster for those days.** Approving converts a shift
 *     row into a leave row, so "this person is rostered กะเช้า on three of these
 *     five days" is the fact the decision actually turns on — and nobody else in
 *     the clinic would notice it. A day already PUNCHED is called out in rose,
 *     because the approval will skip it rather than overwrite the punch.
 *  2. **ลากิจ over three days.** The owner's rule is that the employee may ask, and
 *     that ฝ่ายบุคคล meet them first. So the banner is here, at the moment of
 *     deciding, not only on the form the employee filled in.
 *
 * 🚨 What it deliberately does NOT do: resolve anything silently. It never edits
 * the roster beyond the leave rows themselves, and where an approval cannot be
 * applied to a day it says so and moves on. Same doctrine as the doctor-leave
 * drawer ("approving never mutates the roster") and ขอเวร ("never writes
 * appointment").
 */
export default function LeaveRequestDrawer({ request, employee, branches = [], onClose, onSaved }) {
  const me = useMe()
  const hr = isHR(me)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [confirm, setConfirm] = useState(null)   // 'approve' | 'reject' | 'revert'
  const [rowsLoading, setRowsLoading] = useState(true)
  const [existing, setExisting] = useState([])
  const [result, setResult] = useState(null)

  const meta = leaveTypeMeta(request.type)
  const days = dateRange(request.date_from, request.date_to)
  const needsMeeting = request.type === LEAVE_PERSONAL && Number(request.days) > PERSONAL_REVIEW_DAYS
  const wantsCert = request.type === LEAVE_SICK && Number(request.days) > SICK_CERT_DAYS
  const hasCert = Array.isArray(request.file) && request.file.length > 0
  const warn = applyWarning(request)

  /* What is on the roster across the requested window. A READ — nothing here
   * writes, and the approval is what decides. */
  const loadRows = useCallback(async () => {
    setRowsLoading(true)
    try {
      const r = await apiGet('/api/hr/clockin', {
        employee_id: request.employee_id,
        start_date: request.date_from,
        end_date: request.date_to || request.date_from,
        per_page: 100,
      })
      const list = Array.isArray(r) ? r : (r?.items || [])
      setExisting(list.filter((x) => x?.active !== false))
    } catch {
      setExisting([])
    } finally {
      setRowsLoading(false)
    }
  }, [request.employee_id, request.date_from, request.date_to])

  useEffect(() => { loadRows() }, [loadRows])

  const byDate = new Map()
  for (const row of existing) {
    const d = String(row?.date || '').slice(0, 10)
    if (d && !byDate.has(d)) byDate.set(d, row)
  }
  const punchedDays = days.filter((d) => Number(byDate.get(d)?.clockin?.clockin || 0) > 0)
  const shiftDays = days.filter((d) => byDate.get(d) && Number(byDate.get(d)?.clockin?.clockin || 0) === 0)

  async function decide(status) {
    setConfirm(null)
    setBusy(true)
    setErr('')
    try {
      const r = await postJson(`/api/hr/leave-request/${request.id}`, {
        action: 'decide', status, note: note.trim(),
      })
      setResult(r)
      // Approval writes roster rows, so the result matters — hold the drawer open
      // long enough to show what actually happened, rather than closing on a number
      // nobody read.
      if (status !== 'approved' || !r?.skipped?.length) onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'บันทึกผลไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  async function revert() {
    setConfirm(null)
    setBusy(true)
    setErr('')
    try {
      const out = await postJson(`/api/hr/leave-request/${request.id}`, { action: 'revert', note: note.trim() })
      // A partial revert must not close silently on a green tick — some roster rows
      // are still carrying leave for a request now marked ไม่อนุมัติ, and only a human
      // can fix that.
      if (out?.warning) { setErr(out.warning); setResult(out); return }
      onSaved?.()
    } catch (e) {
      setErr(apiErrorText(e, 'ยกเลิกการอนุมัติไม่สำเร็จ'))
    } finally {
      setBusy(false)
    }
  }

  const pending = request.status === 'pending'
  const approved = request.status === 'approved'

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[520px] bg-white shadow-2xl flex flex-col animate-ss-slide-in">
        {/* header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 ${meta.tone}`}>{meta.label}</span>
              <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 ${statusTone(request.status)}`}>
                {statusLabel(request.status)}
              </span>
            </div>
            <h2 className="mt-1.5 text-[16px] font-semibold text-slate-900 truncate">
              {employee?.code || `#${request.employee_id}`}
              {employee && <span className="ml-2 text-[13px] font-normal text-slate-500">{fullName(employee)}</span>}
            </h2>
            <p className="text-[13px] text-slate-500 tabular-nums">
              {fmtRange(request.date_from, request.date_to)} · {request.days} วัน
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 -mr-1 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {request.reason && (
            <Block title="เหตุผล">
              <p className="text-[13.5px] text-slate-700 whitespace-pre-wrap break-words">{request.reason}</p>
            </Block>
          )}

          {Array.isArray(request.file) && request.file.length > 0 && (
            <Block title="เอกสารแนบ">
              <div className="space-y-1">
                {request.file.map((f) => (
                  <a
                    key={f.uuid || f.location}
                    href={`/api/hr/file/${encodeURI(f.location || '')}`}
                    target="_blank" rel="noreferrer"
                    className="flex items-center gap-1.5 text-[13px] text-brand-600 hover:text-brand-700 hover:underline"
                  >
                    <FileText size={13} /> {f.name || 'เอกสาร'}
                  </a>
                ))}
              </div>
            </Block>
          )}

          {/* 🚨 The reason ฝ่ายบุคคล asked for this feature at all. */}
          {needsMeeting && pending && (
            <Banner tone="amber" icon={Users}>
              ลากิจติดต่อกัน {request.days} วัน (เกิน {PERSONAL_REVIEW_DAYS} วัน) —
              ควรนัดคุยกับพนักงานก่อนอนุมัติ
            </Banner>
          )}
          {wantsCert && !hasCert && pending && (
            <Banner tone="amber" icon={AlertTriangle}>
              ลาป่วยเกิน {SICK_CERT_DAYS} วัน แต่ยังไม่ได้แนบใบรับรองแพทย์
            </Banner>
          )}

          {/* What the approval will actually do to the roster. */}
          <Block title="ตารางเวรในช่วงวันลา">
            {rowsLoading ? (
              <p className="text-[12.5px] text-slate-400"><Loader2 size={12} className="animate-spin inline" /> กำลังโหลด…</p>
            ) : (
              <>
                {punchedDays.length > 0 && (
                  <div className="mb-2 rounded-lg bg-rose-50 ring-1 ring-rose-200 px-2.5 py-2">
                    <p className="text-[12.5px] text-rose-700 font-medium">
                      {punchedDays.length} วันมีการลงเวลาไว้แล้ว — จะข้ามวันเหล่านี้ ไม่ทับการลงเวลา
                    </p>
                    <p className="text-[11.5px] text-rose-600 mt-0.5 tabular-nums">{punchedDays.join(' · ')}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {days.map((d) => {
                    const row = byDate.get(d)
                    const punched = Number(row?.clockin?.clockin || 0) > 0
                    return (
                      <span
                        key={d}
                        title={row ? `${d} — ${row.type}${punched ? ' (ลงเวลาแล้ว)' : ''}` : `${d} — ไม่มีตารางเวร`}
                        className={`px-2 py-1 rounded-lg text-[11.5px] ring-1 tabular-nums ${
                          punched
                            ? 'bg-rose-50 text-rose-700 ring-rose-200'
                            : row
                              ? 'bg-slate-50 text-slate-600 ring-slate-200'
                              : 'bg-white text-slate-400 ring-slate-200 border-dashed'
                        }`}
                      >
                        {d.slice(8)}/{d.slice(5, 7)}
                        {row && <span className="ml-1 text-[10.5px] opacity-70">{shortType(row.type)}</span>}
                      </span>
                    )
                  })}
                </div>
                <p className="mt-2 text-[11.5px] text-slate-400">
                  {shiftDays.length > 0 && `${shiftDays.length} วันมีตารางเวรอยู่แล้ว — จะเปลี่ยนเป็น${meta.label} · `}
                  {days.length - shiftDays.length - punchedDays.length} วันจะสร้างรายการลาใหม่
                </p>
              </>
            )}
          </Block>

          {warn && <Banner tone="amber" icon={AlertTriangle}>{warn}</Banner>}

          {Array.isArray(request.skipped) && request.skipped.length > 0 && (
            <Block title="วันที่ข้ามไป">
              <ul className="space-y-0.5">
                {request.skipped.map((s, i) => (
                  <li key={i} className="text-[12.5px] text-slate-600 tabular-nums">· {s.date} — {skipReason(s)}</li>
                ))}
              </ul>
            </Block>
          )}

          {result?.skipped?.length > 0 && (
            <Banner tone="amber" icon={CalendarClock}>
              ลงตารางเวรแล้ว {result.applied} วัน · ข้าม {result.skipped.length} วัน
            </Banner>
          )}

          {request.decision_note && !pending && (
            <Block title="บันทึกของผู้พิจารณา">
              <p className="text-[13px] text-slate-700 whitespace-pre-wrap break-words">{request.decision_note}</p>
            </Block>
          )}

          {(pending || approved) && (
            <label className="block">
              <span className="block text-[12px] font-medium text-slate-500 mb-1">
                บันทึกถึงพนักงาน <span className="font-normal text-slate-400">· ไม่บังคับ</span>
              </span>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={`${FIELD} resize-y`} />
            </label>
          )}

          {err && <p className="text-[12.5px] text-rose-600">{err}</p>}
        </div>

        {/* footer */}
        <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-end gap-2">
          {pending && (
            <>
              <button
                onClick={() => setConfirm('reject')} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13.5px] font-medium text-rose-600 ring-1 ring-rose-200 hover:bg-rose-50 disabled:opacity-50"
              >
                <Ban size={14} /> ไม่อนุมัติ
              </button>
              <button
                onClick={() => setConfirm('approve')} disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} อนุมัติ
              </button>
            </>
          )}
          {/* HR/Admin only. A branch manager approves; unwinding one — which rewrites
              roster rows that payroll may already have counted — is HR's call. */}
          {approved && hr && (
            <button
              onClick={() => setConfirm('revert')} disabled={busy}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} ยกเลิกการอนุมัติ
            </button>
          )}
          {!pending && !approved && (
            <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ปิด</button>
          )}
        </div>
      </div>

      {confirm === 'approve' && (
        <ConfirmDialog
          title="อนุมัติคำขอลานี้?"
          message={
            `${meta.label} ${fmtRange(request.date_from, request.date_to)} รวม ${request.days} วัน\n\n` +
            `ระบบจะบันทึกวันลาลงในตารางเวรของพนักงานทันที` +
            (shiftDays.length ? `\n· ${shiftDays.length} วันที่มีเวรอยู่แล้ว จะถูกเปลี่ยนเป็น${meta.label}` : '') +
            (punchedDays.length ? `\n· ${punchedDays.length} วันที่ลงเวลาไว้แล้ว จะถูกข้าม (ไม่ทับการลงเวลา)` : '') +
            (needsMeeting ? `\n\n⚠️ ลากิจเกิน ${PERSONAL_REVIEW_DAYS} วัน — ยืนยันว่าได้คุยกับพนักงานแล้ว` : '')
          }
          confirmLabel="อนุมัติ"
          tone={needsMeeting || punchedDays.length ? 'danger' : 'default'}
          onConfirm={() => decide('approved')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'reject' && (
        <ConfirmDialog
          title="ไม่อนุมัติคำขอนี้?"
          message={`พนักงานจะเห็นผลและบันทึกของท่าน${note.trim() ? '' : '\n\nยังไม่ได้เขียนบันทึกถึงพนักงาน — แนะนำให้ระบุเหตุผลสั้น ๆ'}`}
          confirmLabel="ไม่อนุมัติ"
          tone="danger"
          onConfirm={() => decide('rejected')}
          onCancel={() => setConfirm(null)}
        />
      )}
      {confirm === 'revert' && (
        <ConfirmDialog
          title="ยกเลิกการอนุมัติ?"
          message={
            'ระบบจะย้อนตารางเวรกลับเป็นเหมือนก่อนอนุมัติ — วันที่สร้างใหม่จะถูกลบ และวันที่เปลี่ยนประเภทจะถูกเปลี่ยนกลับ\n\n' +
            'หากเดือนนี้คิดเงินเดือนไปแล้ว กรุณาตรวจสอบยอดอีกครั้ง'
          }
          confirmLabel="ยกเลิกการอนุมัติ"
          tone="danger"
          onConfirm={revert}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

function Block({ title, children }) {
  return (
    <div>
      <div className="text-[11.5px] font-medium text-slate-400 mb-1.5">{title}</div>
      {children}
    </div>
  )
}

function Banner({ tone, icon: Icon, children }) {
  const cls = tone === 'rose'
    ? 'bg-rose-50 ring-rose-200 text-rose-800'
    : 'bg-amber-50 ring-amber-200 text-amber-800'
  return (
    <div className={`flex gap-2 rounded-xl ring-1 px-3 py-2.5 ${cls}`}>
      <Icon size={14} className="shrink-0 mt-0.5" />
      <p className="text-[12.5px] leading-relaxed">{children}</p>
    </div>
  )
}
